import React, { useState, useEffect } from 'react';
import AdminNav from '../components/AdminNav';
import { Plus, Edit2, Trash2, X, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { siteContentService, authService } from '../services/api';

function AdminForReaders() {
  const navigate = useNavigate();
  
  const [licenseInfo, setLicenseInfo] = useState('');
  const [editingLicense, setEditingLicense] = useState(false);
  const [tempLicense, setTempLicense] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const licenseResponse = await siteContentService.get('FOR_READERS_LICENSE');
        if (licenseResponse.data && licenseResponse.data.value) {
          setLicenseInfo(licenseResponse.data.value);
          setTempLicense(licenseResponse.data.value);
        }
      } catch (error) {
        console.error('Error fetching license:', error);
      }

      try {
        const benefitsResponse = await siteContentService.get('FOR_READERS_BENEFITS');
        if (benefitsResponse.data && benefitsResponse.data.value) {
          const parsed = JSON.parse(benefitsResponse.data.value);
          if (Array.isArray(parsed)) {
            setBenefits(parsed);
          }
        }
      } catch (error) {
        console.error('Error fetching benefits:', error);
      }
    };
    fetchData();
  }, []);

  const saveLicense = async (value) => {
    try {
      await siteContentService.set('FOR_READERS_LICENSE', { value });
    } catch (error) {
      console.error('Error saving license:', error);
    }
  };

  const saveBenefits = async (value) => {
    try {
      await siteContentService.set('FOR_READERS_BENEFITS', { value: JSON.stringify(value) });
    } catch (error) {
      console.error('Error saving benefits:', error);
    }
  };

  const [benefits, setBenefits] = useState([]);

  const [showBenefitForm, setShowBenefitForm] = useState(false);
  const [editingBenefitId, setEditingBenefitId] = useState(null);
  const [benefitForm, setBenefitForm] = useState({
    title: '',
    description: '',
    icon: '📌',
  });

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  // License handlers
  const handleSaveLicense = async () => {
    setLicenseInfo(tempLicense);
    await saveLicense(tempLicense);
    setEditingLicense(false);
  };

  const handleCancelLicense = () => {
    setTempLicense(licenseInfo);
    setEditingLicense(false);
  };

  // Benefit handlers
  const handleAddBenefit = async (e) => {
    e.preventDefault();
    
    let updatedBenefits;
    if (editingBenefitId) {
      updatedBenefits = benefits.map(benefit =>
        benefit.id === editingBenefitId
          ? { ...benefit, ...benefitForm }
          : benefit
      );
      setEditingBenefitId(null);
    } else {
      const newBenefit = {
        id: Math.max(...benefits.map(b => b.id), 0) + 1,
        ...benefitForm,
      };
      updatedBenefits = [...benefits, newBenefit];
    }

    setBenefits(updatedBenefits);
    await saveBenefits(updatedBenefits);

    setBenefitForm({
      title: '',
      description: '',
      icon: '📌',
    });
    setShowBenefitForm(false);
  };

  const handleEditBenefit = (benefit) => {
    setBenefitForm({
      title: benefit.title,
      description: benefit.description,
      icon: benefit.icon,
    });
    setEditingBenefitId(benefit.id);
    setShowBenefitForm(true);
  };

  const handleDeleteBenefit = async (id) => {
    if (window.confirm('آیا مطمئن هستید؟')) {
      const updatedBenefits = benefits.filter(b => b.id !== id);
      setBenefits(updatedBenefits);
      await saveBenefits(updatedBenefits);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-5xl font-bold mb-2">مدیریت منوی برای خوانندگان</h1>
              <p className="text-xl text-blue-100">ویرایش مزایای دسترسی آزاد و اطلاعات مجوز</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition"
            >
              <LogOut size={20} />
              خروج
            </button>
          </div>
        </div>
      </div>

      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* License Information Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">اطلاعات مجوز</h2>

          {editingLicense ? (
            <div className="space-y-4">
              <textarea
                value={tempLicense}
                onChange={(e) => setTempLicense(e.target.value)}
                rows="8"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 font-medium"
              />
              <div className="flex gap-4">
                <button
                  onClick={handleSaveLicense}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition"
                >
                  ذخیره
                </button>
                <button
                  onClick={handleCancelLicense}
                  className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition"
                >
                  انصراف
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-gray-700 leading-relaxed mb-6 text-lg">{licenseInfo}</p>
              <button
                onClick={() => {
                  setEditingLicense(true);
                  setTempLicense(licenseInfo);
                }}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition"
              >
                <Edit2 size={18} />
                ویرایش اطلاعات مجوز
              </button>
            </div>
          )}
        </div>

        {/* Open Access Benefits Section */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-gray-900">مزایای دسترسی آزاد</h2>
            {!showBenefitForm && (
              <button
                onClick={() => setShowBenefitForm(true)}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-bold hover:from-green-700 hover:to-green-800 transition"
              >
                <Plus size={20} />
                افزودن مزیت جدید
              </button>
            )}
          </div>

          {/* Benefit Form */}
          {showBenefitForm && (
            <div className="bg-gray-50 rounded-lg p-6 mb-8 border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingBenefitId ? 'ویرایش مزیت' : 'افزودن مزیت جدید'}
                </h3>
                <button
                  onClick={() => {
                    setShowBenefitForm(false);
                    setEditingBenefitId(null);
                  }}
                  className="p-2 hover:bg-gray-200 rounded-lg transition"
                >
                  <X size={24} className="text-gray-600" />
                </button>
              </div>

              <form onSubmit={handleAddBenefit} className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">عنوان</label>
                  <input
                    type="text"
                    value={benefitForm.title}
                    onChange={(e) => setBenefitForm({ ...benefitForm, title: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="عنوان مزیت"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">توضیح</label>
                  <textarea
                    value={benefitForm.description}
                    onChange={(e) => setBenefitForm({ ...benefitForm, description: e.target.value })}
                    required
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="توضیح مزیت"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">آیکون (Emoji)</label>
                  <input
                    type="text"
                    value={benefitForm.icon}
                    onChange={(e) => setBenefitForm({ ...benefitForm, icon: e.target.value })}
                    maxLength="2"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 text-2xl"
                    placeholder="🔓"
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition"
                  >
                    {editingBenefitId ? 'ذخیره تغییرات' : 'افزودن مزیت'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowBenefitForm(false);
                      setEditingBenefitId(null);
                    }}
                    className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition"
                  >
                    انصراف
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map(benefit => (
              <div key={benefit.id} className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 border border-green-200 hover:shadow-lg transition">
                <div className="flex items-start justify-between mb-4">
                  <div className="text-4xl">{benefit.icon}</div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditBenefit(benefit)}
                      className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteBenefit(benefit.id)}
                      className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-700 text-sm leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>

          {benefits.length === 0 && !showBenefitForm && (
            <div className="text-center py-12 text-gray-500">
              <p>هیچ مزیتی برای نمایش وجود ندارد</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminForReaders;
